import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import MaterialIcon from "../../../shared/components/MaterialIcon";
import { fetchDeletionCaseStats } from "../casesApi";
import useClientAccountStatus from "../hooks/useClientAccountStatus";
import useSubscriptionBenefits from "../hooks/useSubscriptionBenefits";
import ProfessionalBenefitsBanner from "../components/ProfessionalBenefitsBanner";
import CustomOfferPlanCard from "../components/CustomOfferPlanCard";
import { fetchCurrentOffer } from "../customOffersApi";
import { setBillingPlan } from "../billingPlanStorage";
import {
  ACCOUNT_STATUS,
  accountStatusLabel,
  billingPlanForAccountStatus,
  isPaidAccount,
  normalizeAccountStatus,
} from "../../../lib/accountStatus";
import {
  useTranslation,
  getFreeTierVolumeBands,
  getFreeTierFeatures,
  getSubscriberFeatures,
  getEnterpriseFeatures,
  getPlanComparisonRows,
  BASIC_PLAN,
  SUBSCRIBER_PLAN,
  formatEuro,
  getDefaultCheckout,
} from "../../../i18n";

function StatusBadge({ status }) {
  const { t } = useTranslation();
  const label = accountStatusLabel(status, t);
  const paid = isPaidAccount(status);

  return (
    <span
      className={[
        "rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider",
        paid
          ? "bg-secondary-container text-on-secondary-container"
          : "bg-error-container text-on-error-container",
      ].join(" ")}
    >
      {t("billing.statusLabel", { status: label })}
    </span>
  );
}

function FeatureRow({ included, text, bold = false }) {
  return (
    <div className="flex items-start gap-3">
      <MaterialIcon
        name={included ? "check_circle" : "cancel"}
        filled={included}
        className={included ? "text-lg text-secondary" : "text-lg text-outline-variant"}
      />
      <span
        className={[
          "text-sm",
          included ? (bold ? "font-bold" : "font-medium") : "text-outline",
        ].join(" ")}
      >
        {text}
      </span>
    </div>
  );
}

function BasicPlanCard({ isCurrentPlan, onChoose }) {
  const { t } = useTranslation();
  const volumeBands = useMemo(() => getFreeTierVolumeBands(t), [t]);
  const features = useMemo(() => getFreeTierFeatures(t), [t]);

  return (
    <div className="tonal-card flex flex-col rounded-2xl border border-outline-variant p-8">
      <div className="mb-6">
        <span className="text-xs font-bold uppercase tracking-widest text-on-surface-variant">
          {t("billing.starter")}
        </span>
        <h3 className="mt-1 font-headline-lg text-headline-lg">{t("billing.basicPlan")}</h3>
      </div>

      <div className="mb-6">
        <div className="flex items-baseline gap-1">
          <span className="font-headline-xl text-4xl font-bold">
            {formatEuro(BASIC_PLAN.monthlyPrice)}
          </span>
          <span className="font-body-md text-on-surface-variant">{t("billing.monthly")}</span>
        </div>
        <p className="mt-2 text-sm text-on-surface-variant">{t("billing.basicTagline")}</p>
      </div>

      <div className="mb-6">
        <div className="flex flex-col gap-2">
          {volumeBands.map((band, index) => (
            <div
              key={band.range}
              className={[
                "flex items-center justify-between",
                index < volumeBands.length - 1
                  ? "border-b border-outline-variant pb-1"
                  : "",
              ].join(" ")}
            >
              <span className="text-xs font-bold uppercase text-on-surface-variant">
                {band.range}
              </span>
              <span className="text-lg font-bold">
                {formatEuro(band.pricePerReview)}{" "}
                <span className="text-[10px] font-normal">{t("billing.perEach")}</span>
              </span>
            </div>
          ))}
        </div>
        <p className="mt-3 text-sm italic text-on-surface-variant">
          {t("billing.volumePricing")}
        </p>
      </div>

      <div className="mb-10 flex-grow space-y-4">
        {features.map((feature) => (
          <FeatureRow
            key={feature.text}
            included={feature.included}
            text={feature.text}
          />
        ))}
      </div>

      {isCurrentPlan ? (
        <button
          type="button"
          disabled
          className="w-full cursor-default rounded-xl border border-outline-variant px-4 py-3 font-bold text-primary"
        >
          {t("billing.currentPlan")}
        </button>
      ) : (
        <button
          type="button"
          onClick={onChoose}
          className="w-full rounded-xl border border-outline-variant px-4 py-3 font-bold text-primary transition-colors hover:bg-surface-container"
        >
          {t("billing.chooseBasic")}
        </button>
      )}
    </div>
  );
}

function PremiumPlanCard({ isCurrentPlan, onUpgrade }) {
  const { t } = useTranslation();
  const features = useMemo(() => getSubscriberFeatures(t), [t]);

  return (
    <div className="tonal-card relative flex flex-col overflow-hidden rounded-2xl border-2 border-secondary bg-surface-container-lowest p-8">
      {!isCurrentPlan && (
        <div className="absolute right-0 top-0">
          <div className="translate-x-10 translate-y-2 rotate-45 transform bg-secondary px-10 py-1 text-[10px] font-bold uppercase text-on-primary">
            {t("billing.mostPopular")}
          </div>
        </div>
      )}

      <div className="mb-6">
        <span className="text-xs font-bold uppercase tracking-widest text-secondary">
          {t("billing.growth")}
        </span>
        <h3 className="mt-1 font-headline-lg text-headline-lg">
          {t("billing.premiumPlan")}
        </h3>
      </div>

      <div className="mb-8">
        <div className="flex items-baseline gap-1">
          <span className="font-headline-xl text-4xl font-bold text-secondary">
            {formatEuro(SUBSCRIBER_PLAN.monthlyPrice)}
          </span>
          <span className="font-body-md text-on-surface-variant">{t("billing.monthly")}</span>
        </div>
        <p className="mt-2 text-sm text-on-surface-variant">{t("billing.proTagline")}</p>
      </div>

      <div className="mb-10 flex-grow space-y-4">
        {features.map((feature, index) => (
          <FeatureRow key={feature} included text={feature} bold={index === 0} />
        ))}
      </div>

      {isCurrentPlan ? (
        <button
          type="button"
          disabled
          className="w-full cursor-default rounded-xl border border-outline-variant px-4 py-3 font-bold text-primary"
        >
          {t("billing.currentPlan")}
        </button>
      ) : (
        <button
          type="button"
          onClick={onUpgrade}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-secondary px-4 py-4 font-bold text-on-primary transition-opacity hover:opacity-90"
        >
          {t("billing.upgradeProfessional")}
          <MaterialIcon name="arrow_forward" className="text-base" />
        </button>
      )}
    </div>
  );
}

function EnterprisePlanCard({ isCurrentPlan, onContact }) {
  const { t } = useTranslation();
  const features = useMemo(() => getEnterpriseFeatures(t), [t]);

  return (
    <div className="tonal-card flex flex-col rounded-2xl border border-outline-variant bg-surface-container-low p-8">
      <div className="mb-6">
        <span className="text-xs font-bold uppercase tracking-widest text-on-surface-variant">
          {t("billing.scale")}
        </span>
        <h3 className="mt-1 font-headline-lg text-headline-lg">
          {t("billing.enterprisePlan")}
        </h3>
      </div>

      <div className="mb-8">
        <div className="flex items-baseline gap-1">
          <span className="font-headline-xl text-4xl font-bold">
            {t("billing.individualPrice")}
          </span>
        </div>
        <p className="mt-2 text-sm text-on-surface-variant">
          {t("billing.enterpriseTagline")}
        </p>
      </div>

      <div className="mb-10 flex-grow space-y-4">
        {features.map((feature, index) => (
          <FeatureRow key={feature} included text={feature} bold={index === 0} />
        ))}
      </div>

      {isCurrentPlan ? (
        <button
          type="button"
          disabled
          className="w-full cursor-default rounded-xl border border-outline-variant px-4 py-3 font-bold text-primary"
        >
          {t("billing.currentPlan")}
        </button>
      ) : (
        <button
          type="button"
          onClick={onContact}
          className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-primary px-4 py-3 font-bold text-primary transition-colors hover:bg-primary hover:text-on-primary"
        >
          {t("billing.contactSales")}
          <MaterialIcon name="forum" className="text-base" />
        </button>
      )}
    </div>
  );
}

/** Placeholder for the third plan slot while we check for a personalised package. */
function PlanCardSkeleton() {
  return (
    <div className="tonal-card flex flex-col rounded-2xl border border-outline-variant p-8" aria-hidden>
      <div className="mb-6 space-y-2">
        <div className="h-3 w-16 animate-pulse rounded bg-surface-container-high" />
        <div className="h-7 w-40 animate-pulse rounded bg-surface-container-high" />
      </div>
      <div className="mb-8 h-10 w-32 animate-pulse rounded bg-surface-container-high" />
      <div className="mb-10 flex-grow space-y-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-4 w-full animate-pulse rounded bg-surface-container" />
        ))}
      </div>
      <div className="h-12 w-full animate-pulse rounded-xl bg-surface-container-high" />
    </div>
  );
}

function TrialInfoStrip() {
  const { t } = useTranslation();

  return (
    <div className="mx-auto mb-8 flex max-w-6xl items-start gap-3 rounded-2xl border border-secondary/30 bg-secondary-container/25 p-5">
      <MaterialIcon name="timer" filled className="mt-0.5 shrink-0 text-secondary" />
      <div>
        <p className="font-bold text-on-surface">{t("billing.trialNoteTitle")}</p>
        <p className="mt-0.5 text-sm text-on-surface-variant">{t("billing.trialNote")}</p>
      </div>
    </div>
  );
}

function PlanComparisonTable() {
  const { t } = useTranslation();
  const rows = useMemo(() => getPlanComparisonRows(t), [t]);

  return (
    <div className="tonal-card overflow-hidden rounded-2xl border border-outline-variant">
      <div className="flex items-center justify-between border-b border-outline-variant p-8">
        <div>
          <h3 className="font-headline-lg text-headline-lg">{t("billing.planComparison")}</h3>
          <p className="mt-1 text-sm text-on-surface-variant">{t("billing.comparisonDesc")}</p>
        </div>
        <MaterialIcon name="analytics" className="text-3xl text-outline-variant" />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="bg-surface-container-low">
              <th className="border-b border-outline-variant p-6 text-xs font-bold uppercase tracking-widest text-on-surface-variant">
                {t("billing.featureColumn")}
              </th>
              <th className="border-b border-outline-variant p-6 text-xs font-bold uppercase tracking-widest text-on-surface-variant">
                {t("billing.basicPlan")}
              </th>
              <th className="border-b border-outline-variant p-6 text-xs font-bold uppercase tracking-widest text-secondary">
                {t("billing.premiumPlan")}
              </th>
              <th className="border-b border-outline-variant p-6 text-xs font-bold uppercase tracking-widest text-on-surface-variant">
                {t("billing.enterprisePlan")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-outline-variant">
            {rows.map((row) => (
              <tr
                key={row.feature}
                className="transition-colors hover:bg-surface-container-low"
              >
                <td className="p-6 text-sm font-bold">{row.feature}</td>
                <td
                  className={[
                    "p-6 text-sm",
                    row.freeTierMuted ? "italic text-outline" : "",
                  ].join(" ")}
                >
                  {row.freeTier}
                </td>
                <td
                  className={[
                    "p-6 text-sm",
                    row.professionalBold ? "font-bold" : "",
                    row.professionalHighlight ? "font-medium text-secondary" : "",
                  ].join(" ")}
                >
                  {row.professional}
                </td>
                <td className="p-6 text-sm">{row.enterprise}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FooterPromoBanner({ onGoToPayment }) {
  const { t } = useTranslation();

  return (
    <div className="relative mt-12 flex flex-col items-center justify-between gap-6 overflow-hidden rounded-2xl bg-primary-container p-8 md:flex-row">
      <div className="relative z-10">
        <h4 className="font-headline-md text-headline-md text-white">{t("billing.ctaTitle")}</h4>
        <p className="mt-1 text-sm text-on-primary-container">{t("billing.ctaSubtitle")}</p>
      </div>
      <div className="relative z-10 flex items-center gap-6">
        <div className="hidden text-right sm:block">
          <span className="block font-bold text-white">
            {formatEuro(SUBSCRIBER_PLAN.monthlyPrice)}
            {t("billing.monthly")}
          </span>
          <span className="text-[10px] font-bold uppercase tracking-wider text-on-primary-container">
            {t("billing.billedMonthly")}
          </span>
        </div>
        <button
          type="button"
          onClick={onGoToPayment}
          className="rounded-xl bg-secondary px-8 py-3 font-bold text-on-primary shadow-lg shadow-black/20 transition-colors hover:bg-on-secondary-container"
        >
          {t("billing.goToPayment")}
        </button>
      </div>
    </div>
  );
}

const ClientBillingPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { accountStatus, isPaid, isPremium, loading } = useClientAccountStatus();
  const {
    freeRemaining,
    freeAllowance,
    freeUsed,
    periodEnd,
  } = useSubscriptionBenefits(accountStatus, { enabled: isPremium });
  const [caseStats, setCaseStats] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [checkoutNotice, setCheckoutNotice] = useState("");

  // Personalised package (admin-created): replaces the Enterprise card when present.
  const [offerState, setOfferState] = useState({
    loading: true,
    offer: null,
    isAccountOwner: true,
    paymentsAvailable: true,
  });

  useEffect(() => {
    let active = true;
    fetchCurrentOffer()
      .then((data) => {
        if (active) setOfferState({ loading: false, ...data });
      })
      .catch(() => {
        if (active) setOfferState((s) => ({ ...s, loading: false, offer: null }));
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (searchParams.get("checkout") !== "cancelled") return;
    setCheckoutNotice(t("customOffer.checkoutCancelled"));
    const next = new URLSearchParams(searchParams);
    next.delete("checkout");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, t]);

  const loadCaseStats = useCallback(async () => {
    try {
      const data = await fetchDeletionCaseStats();
      setCaseStats(data);
    } catch {
      setCaseStats(null);
    }
  }, []);

  useEffect(() => {
    loadCaseStats();
  }, [loadCaseStats]);

  const activeCases = (caseStats?.pending ?? 0) + (caseStats?.inProgress ?? 0);

  const includedDeletions = freeAllowance || SUBSCRIBER_PLAN.freeDeletionsPerMonth;

  const startReviewSelection = () => {
    const plan = billingPlanForAccountStatus(accountStatus);
    setBillingPlan(plan);
    navigate("/dashboard/client/reviews", {
      state: { billingPlan: plan, openView: "critical" },
    });
  };

  const goToPayment = (plan = "premium") => {
    navigate("/dashboard/client/payment", {
      state: getDefaultCheckout(t, plan),
    });
  };

  const normalizedStatus = normalizeAccountStatus(accountStatus);

  return (
    <main className="bg-background px-10 pb-16 pt-10 font-body-md text-on-surface">
      <div className="mx-auto max-w-[1400px]">
        <div className="mb-10 flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <div>
            <div className="mb-2 flex items-center gap-3">
              <h2 className="font-headline-xl text-headline-xl">{t("billing.title")}</h2>
              {!loading && <StatusBadge status={accountStatus} />}
            </div>
            <p className="max-w-2xl font-body-md text-on-surface-variant">
              {isPaid ? t("billing.paidDesc") : t("billing.unpaidDesc")}
            </p>
          </div>

          <div className="flex gap-4">
            <div className="flex min-w-[180px] flex-col gap-1 rounded-xl border border-outline-variant bg-surface-container-lowest p-4">
              <span className="text-[11px] font-bold uppercase tracking-widest text-on-surface-variant">
                {t("billing.activeCases")}
              </span>
              <div className="flex items-baseline gap-2">
                <span className="font-headline-lg text-headline-lg">
                  {isPremium ? includedDeletions - freeRemaining : activeCases}
                </span>
                <span className="text-sm text-on-surface-variant">
                  {t("billing.includedOf", { count: includedDeletions })}
                </span>
              </div>
            </div>
          </div>
        </div>

        <TrialInfoStrip />

        {checkoutNotice ? (
          <div className="mx-auto mb-8 flex max-w-6xl items-center gap-3 rounded-2xl border border-outline-variant bg-surface-container-low px-5 py-4 text-sm text-on-surface-variant">
            <MaterialIcon name="info" className="text-secondary" />
            {checkoutNotice}
          </div>
        ) : null}

        <div className="mx-auto mb-16 grid max-w-6xl grid-cols-1 gap-8 md:grid-cols-3">
          <BasicPlanCard
            isCurrentPlan={normalizedStatus === ACCOUNT_STATUS.BASIC}
            onChoose={() => goToPayment("basic")}
          />
          <PremiumPlanCard
            isCurrentPlan={normalizedStatus === ACCOUNT_STATUS.PREMIUM}
            onUpgrade={() => goToPayment("premium")}
          />
          {offerState.loading ? (
            <PlanCardSkeleton />
          ) : offerState.offer ? (
            <CustomOfferPlanCard
              offer={offerState.offer}
              isAccountOwner={offerState.isAccountOwner}
              paymentsAvailable={offerState.paymentsAvailable}
            />
          ) : (
            <EnterprisePlanCard
              isCurrentPlan={normalizedStatus === ACCOUNT_STATUS.ENTERPRISE}
              onContact={() =>
                navigate("/dashboard/client/support", {
                  state: {
                    presetCategory: "support",
                    presetSubject: t("customOffer.supportSubject"),
                    presetMessage: t("customOffer.supportMessage"),
                  },
                })
              }
            />
          )}
        </div>

        <PlanComparisonTable />

        {!isPremium && <FooterPromoBanner onGoToPayment={() => goToPayment("premium")} />}

        {isPremium && (
          <div className="mb-10 max-w-2xl">
            <ProfessionalBenefitsBanner
              freeRemaining={freeRemaining}
              freeAllowance={includedDeletions}
              freeUsed={freeUsed}
              periodEnd={periodEnd}
            />
          </div>
        )}

        {isPremium && (
          <div className="mt-12 flex flex-col items-start justify-between gap-4 rounded-2xl border border-secondary/30 bg-surface-container-low p-8 md:flex-row md:items-center">
            <div>
              <h4 className="font-headline-md text-headline-md">{t("billing.startNewCase")}</h4>
              <p className="mt-1 text-sm text-on-surface-variant">
                {t("billing.freeDeletionsRemaining", {
                  count: freeRemaining,
                  price: formatEuro(SUBSCRIBER_PLAN.additionalDeletionPrice),
                })}
              </p>
            </div>
            <button
              type="button"
              onClick={startReviewSelection}
              className="flex items-center justify-center gap-2 rounded-xl bg-secondary px-8 py-3 font-bold text-on-primary transition-opacity hover:opacity-90"
            >
              {t("billing.selectReviews")}
              <MaterialIcon name="arrow_forward" className="text-base" />
            </button>
          </div>
        )}
      </div>
    </main>
  );
};

export default ClientBillingPage;
